import type {SessionState} from 'src/v2/state/store';
import {
  isExtensionWalletProvider,
  type Wallet,
} from '@stripe-internal/wallet-connect-wrapper';
import type {Mode} from 'src/state/usePayinSessionContext';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import {getUrlParams} from 'src/utils/utils';
import type {SupportedCurrency} from 'src/v2/state/types';
import {isAnyCryptoToken} from 'src/v2/components/anyCrypto/tokenSelect/tokenSelectConstants';

type Params = {
  sessionState: SessionState['state'];
  activeWallet: Wallet | null;
  mode: Mode;
  enablePayWithAnyCrypto: boolean;
  enablePayWithAnyCryptoWalletConnect: boolean;
  walletConnectSupportedCurrencies: SupportedCurrency[];
};

type InitialScreenState = {
  screen: SessionState['screen'];
  newWallet?: SessionState['newWallet'];
  selectedToken?: SessionState['anyCryptoDeposit']['selectedToken'];
};

const getSelectedWalletOption = (selectedWallet: string | null) => {
  if (!selectedWallet) {
    return null;
  }

  if (isExtensionWalletProvider(selectedWallet)) {
    return WalletRegistry.getWalletOption({
      provider: selectedWallet,
    });
  }

  return WalletRegistry.getWalletOption({
    provider: 'WalletConnect',
    key: selectedWallet,
  });
};

export function getInitialScreen({
  sessionState,
  activeWallet,
  mode,
  enablePayWithAnyCrypto,
  enablePayWithAnyCryptoWalletConnect,
  walletConnectSupportedCurrencies,
}: Params): InitialScreenState {
  if (enablePayWithAnyCrypto) {
    switch (sessionState) {
      case 'fulfillment_complete':
      case 'purchase_complete':
      case 'await_deposit':
      case 'error':
      case 'failed':
        return {screen: 'ANY_CRYPTO_PROGRESS'};
      case 'canceled':
        return {screen: 'CANCELED'};
      default: {
        if (enablePayWithAnyCryptoWalletConnect) {
          const {selectedToken, selectedWallet} = getUrlParams();
          const walletOption = getSelectedWalletOption(selectedWallet);

          if (
            isAnyCryptoToken(selectedToken) &&
            selectedWallet &&
            walletConnectSupportedCurrencies.some(
              (currency) => currency.assetCode === selectedToken,
            ) &&
            walletOption
          ) {
            if (activeWallet) {
              return {
                screen: 'PAY',
                selectedToken,
              };
            }

            return {
              screen: 'NETWORK_SELECT',
              newWallet: {
                walletOption,
                networkType:
                  walletOption.provider === 'WalletConnect' ? 'evm' : null,
              },
              selectedToken,
            };
          }
        }

        return {screen: 'TOKEN_SELECT'};
      }
    }
  }

  switch (sessionState) {
    case 'fulfillment_complete':
    case 'setup_complete':
      return {screen: 'SUCCESS'};
    case 'canceled':
      return {screen: 'CANCELED'};
    case 'purchase_complete':
      return {screen: 'PAY'};
    // PayinSession is 'error' while SetupSession is 'failed'
    case 'error':
    case 'failed':
      return {screen: 'ERROR'};
    case 'checkout':
    default:
      // For checkout state and any other states, check wallet presence
      if (!activeWallet) {
        const installed = WalletRegistry.getInstalledExtensionWallets();
        if (installed.length === 1) {
          return {
            screen: 'NETWORK_SELECT',
            newWallet: {
              walletOption: installed[0],
              networkType: null,
            },
          };
        }
        return {screen: 'WALLET_SELECT'};
      }
      return {screen: mode === 'pay' ? 'PAY' : 'SETUP'};
  }
}
